/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { jsx, css } from '@emotion/react';
import useAxios from 'axios-hooks';
import { Spinner, Pivot, PivotItem, ChoiceGroup } from '@fluentui/react';
import { Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import {
    validationSchema,
    mapInitialValues,
    route,
    fetchRoute,
    basePath,
    successMsg,
    extraValidation,
    setErrorMsg,
    buildCustomErrorMsg,
    rootCss,
    unsavedMsg,
    hasMeaningfulChanges,
} from '../Helpers/CopyAndFunctions';
import useHandleSave from '../../../../Util/useHandleSave';
import FormStepWrapper from '../../../../Forms/FormStepWrapper';
import RewardCtaFields from '../Components/RewardCtaFields';
import ClientCobrandStep from '../Components/ClientCobrandStep';
import { useInitializeRewardCtas } from '../Helpers/useInitializeRewardCtas';
import MessageBarComponent from '../../../../Util/MessageBarComponent';
import Toggle from '../../../../Forms/Toggle';
import BasicInfoFields from './../Components/BasicInfoFields';
import FeatureSummary from '../../../../Util/FeatureSummary';

const integratedRewardsSummary = [
    {
        selected: "0",
        label: "Select a Client/Cobrand",
        summary: "Choose the client or cobrand for this integrated rewards dashboard. Each dashboard is configured for a specific client or cobrand.",
        imageUrlPath: "integrated-rewards-select",
    },
    {
        selected: "1",
        label: "Build the Page",
        summary: "Configure the dashboard layout. Use the default template or enable custom dashboard to set a custom title, subtitle, and additional content.",
        imageUrlPath: "integrated-rewards-build",
    },
    {
        selected: "2",
        label: "Pick your CTAs",
        summary: "Select which reward call-to-action cards to display on this dashboard. Each CTA links users to a specific reward partner program.",
        imageUrlPath: "integrated-rewards-ctas",
    },
    {
        selected: "3",
        label: "Publish",
        summary: "Control the dashboard's visibility by setting its published status. Draft dashboards are not visible to end users.",
        imageUrlPath: "integrated-rewards-publish",
    },
];


const WizardPage = () => {
    const history = useHistory ();
    const { level, dashboardId: idFromParams } = useParams();
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [customMsg, setCustomMsg] = useState('');
    const [selectedKey, setSelectedKey] = useState(idFromParams ? '1' : '0');
    const isNew = !idFromParams;
    
    const [{ data: availableCobrands, loadingAvailableCobrands }] = useAxios('/integrated-rewards/dashboard/available-cobrands');
    const cobrandsOptions = availableCobrands && availableCobrands.map(u => ({ value: u.cobrandId, label: u.cobrandName })); 
    const [{ data: clients, loadingClients }] = useAxios('/integrated-rewards/dashboard/available-clients');
    const clientsOptions = clients && clients.map(u => ({ value: u.clientId, label: u.clientName }));
    const [{ data: eligibleCobrands, loadingEligibleCobrands }] = useAxios('/integrated-rewards/dashboard/cobrand-settings');

    const [{ data: allRewards, loading: allRewardsLoading }] = useAxios('integrated-rewards/all?bustCache=true');

    const [{ data: integratedRewardData, dataLoading }, getData] = useAxios(
        { url: `${fetchRoute(level)}/${idFromParams}?bustCache=true`, method: "get" },
        { manual: true }
    );

    const loading = loadingAvailableCobrands || loadingClients || loadingEligibleCobrands || allRewardsLoading || dataLoading;

    const [{}, submitForm] = useAxios(
        { url: `${route}/upsert`, method: "post" },
        { manual: true }
    );

    useEffect(() => {
        if (idFromParams && level) {
            getData();
        }
    }, [idFromParams, level]);


    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const validationErrors = extraValidation(values);
            if (validationErrors.length > 0) {
                const msg = validationErrors.join(" ");
                setCustomMsg(msg);
                setShowError(true);
                return;
            }

            const submitValues = {
                ...values,
                integratedRewardsClientDashboardId: values.integratedRewardsClientDashboardId || 0,
                integratedRewardsDashboardId: values.integratedRewardsDashboardId || 0,
                cobrandId: values.isClient ? 0 : values.cobrandId,
                clientId: values.isCobrand ? 0 : values.clientId
            };
            const currentRewardIds = (values.rewardIds || []).slice();
            const selectedRewards = (values.ctaModel || []).filter((r) =>
                currentRewardIds.includes(r.integratedRewardId)
            );

            if (selectedRewards.length === 0) {
                setShowError(true);
                return;
            }

            submitValues.rewards = selectedRewards;

            let result;
            result = await submitForm({ data: submitValues });

            const ok = result && result.status === 200;

            setShowSuccess(ok);
            setShowError(!ok);

            if (ok) {
                resetForm({ values: submitValues });
                setSubmitting(false);
            }

            
        } catch (error) {
            console.log("Submit failed:", error);
            setShowError(true);
        }

        setTimeout(() => {
            setShowSuccess(false);
            if (isNew) {
                history.push(`${basePath}`);
            } else {
                getData();
            }
        }, 5000);
    };

    const isReady = (!!integratedRewardData && !loading) || !idFromParams;

    if (!isReady) {
        return <Spinner className='absolute-center' text='Loading Integrated Reward Feature...' />;
    }

    return (
        <Formik
            validateOnMount={true}
            enableReinitialize  
            initialValues={mapInitialValues({
                ...(integratedRewardData || {}),
                allRewards: allRewards,
                eligibleCobrands: eligibleCobrands,

            })}
            onSubmit={handleSubmit}
            isInitialValid={false}
            validationSchema={validationSchema}
        >
            {({ errors, values, isValid, handleSubmit, setFieldValue, isSubmitting }) => {
                const handleSave = useHandleSave(setShowError);
                useInitializeRewardCtas(values, setFieldValue);
                const meaningful = hasMeaningfulChanges(values);
                const canSubmit = isValid && meaningful && !isSubmitting;
                const summaryObj = integratedRewardsSummary.find(s => String(s.selected) === String(selectedKey));

                return values ? (
                    <div css={css`max-width: 800px;`}>
                        <MessageBarComponent 
                            message={buildCustomErrorMsg(showError, customMsg)}
                            showMessage={showError} 
                            setShowMessage={setShowError} 
                            isMultiline
                        />
                        <FormStepWrapper
                            handleSave={() => handleSave(canSubmit, handleSubmit)}
                            showSuccess={showSuccess}
                            showError={showError}
                            successMessage={successMsg}
                            errorMessage={setErrorMsg(showError, errors, customMsg)}
                            unsavedMsg={unsavedMsg}
                            rootCss={rootCss}
                            showNavigation={true}
                            firstStep={idFromParams ? 1 : 0}
                            lastStep={3}
                            setShowSuccess={setShowSuccess}
                            setShowError={setShowError}
                            selectedKey={selectedKey}
                            setSelectedKey={setSelectedKey}
                            disabledSave={!canSubmit}
                        >
                            <Pivot selectedKey={selectedKey} onLinkClick={(item) => setSelectedKey(item?.props?.itemKey)}>
                                {(!idFromParams || idFromParams === 0) && 
                                <PivotItem headerText={`Select a ${level === 'client' ? 'Client' : 'Cobrand'}`} itemKey='0'>
                                    <ClientCobrandStep 
                                        level={level} 
                                        cobrands={availableCobrands} 
                                        cobrandsOptions={cobrandsOptions}
                                        clientsOptions={clientsOptions}
                                        setFieldValue={setFieldValue}
                                        values={values}
                                    />
                                </PivotItem>
                                 }
                                <PivotItem headerText='Build the Page' itemKey='1'>
                                    <div style={{ marginBottom: 16 }}>
                                        <Toggle
                                            label="Use Default Template"
                                            checked={!values.hasCustomDashboard}
                                            onChange={(e, checked) => setFieldValue('hasCustomDashboard', !checked)}
                                        />
                                    </div>
                                    {values.hasCustomDashboard && (
                                        <BasicInfoFields />
                                    )}
                                </PivotItem>
                                <PivotItem headerText='Pick your CTAs' itemKey='2'>
                                    <RewardCtaFields 
                                        allRewards={values.allRewards}
                                        rewardIds={values.rewardIds}
                                        setFieldValue={setFieldValue}
                                    />
                                </PivotItem>
                                <PivotItem headerText='Publish' itemKey='3'>
                                    <ChoiceGroup
                                        label="Published Status"
                                        selectedKey={values.isPublished ? 1 : 0}
                                        options={[
                                            { key: 1, text: 'Published' },
                                            { key: 0, text: 'Draft' }
                                        ]}
                                        onChange={(e, option) => setFieldValue('isPublished', option.key === 1)}
                                    />
                                </PivotItem>
                            </Pivot>
                        </FormStepWrapper>
                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}
                    </div>
                ) : null;
            }}
        </Formik>
    );
};

export default WizardPage;
